import { useTheme } from '@material-ui/core/styles';
import { DependencyGraphTypes } from '@backstage/core-components';
import { EntityNode } from '@backstage/plugin-catalog-graph';

// Kyndryl brand color palette
const K = {
  springGreen:             '#4CDD84',
  digitalDarkSpringGreen:  '#187E3F',
  deepForest:              '#042315',
  spruce:                  '#29707A',
  darkSpruce:              '#1D5560',
  darkStone:               '#3D3C3C',
  earth:                   '#9E9287',
  cloud:                   '#F2F1EE',
  digitalDarkEarth:        '#565049',
};

const NODE_WIDTH  = 180;
const NODE_HEIGHT = 56;

/**
 * Per-kind color mapping: [background, primaryText, kindLabelText]
 * Each kind gets a distinct color so the graph is readable at a glance.
 */
type KindColorEntry = [bg: string, text: string, kindText: string];

const KIND_COLORS_LIGHT: Record<string, KindColorEntry> = {
  component: [K.digitalDarkSpringGreen, K.cloud,       K.springGreen],
  system:    [K.deepForest,             K.springGreen,  K.digitalDarkSpringGreen],
  resource:  [K.spruce,                 K.cloud,        '#93C9CE'],
  group:     [K.darkStone,              K.cloud,        K.earth],
  user:      [K.digitalDarkEarth,       K.cloud,        K.earth],
  api:       [K.darkSpruce,             K.cloud,        '#93C9CE'],
  domain:    [K.deepForest,             K.springGreen,  K.digitalDarkSpringGreen],
  location:  [K.earth,                  K.deepForest,   '#5A5048'],
};

const KIND_COLORS_DARK: Record<string, KindColorEntry> = {
  component: [K.digitalDarkSpringGreen, K.cloud,       K.springGreen],
  system:    ['#0B3D1E',                K.springGreen,  K.digitalDarkSpringGreen],
  resource:  [K.darkSpruce,             K.cloud,       '#93C9CE'],
  group:     ['#2A2929',                K.cloud,       K.earth],
  user:      ['#3A3530',                K.cloud,       K.earth],
  api:       ['#152C34',                K.cloud,       '#93C9CE'],
  domain:    ['#0B3D1E',                K.springGreen, K.digitalDarkSpringGreen],
  location:  [K.digitalDarkEarth,       K.cloud,       K.earth],
};

const FALLBACK_LIGHT: KindColorEntry = [K.digitalDarkEarth, K.cloud, K.earth];
const FALLBACK_DARK:  KindColorEntry = ['#2A2929',           K.cloud, K.earth];

export type CatalogNodeData = EntityNode;

type KyndrylCatalogNodeProps = DependencyGraphTypes.RenderNodeProps<EntityNode>;

/**
 * Custom Backstage catalog graph node renderer using Kyndryl brand colors.
 * Returns SVG elements so that the parent DependencyGraph getBBox() measurement works correctly.
 * Pass this as the `renderNode` prop to `EntityCatalogGraphCard` and `CatalogGraphPage`.
 */
export function KyndrylCatalogNode({ node }: KyndrylCatalogNodeProps): JSX.Element {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const kind = (node.kind ?? 'component').toLowerCase();
  const colorMap = isDark ? KIND_COLORS_DARK : KIND_COLORS_LIGHT;
  const [bgColor, textColor, kindTextColor] = colorMap[kind] ?? (isDark ? FALLBACK_DARK : FALLBACK_LIGHT);

  const label = node.title ?? node.name ?? '';
  const isFocused = !!node.focused;

  // SVG node — must return SVG elements so that the parent <g> getBBox() can measure
  // dimensions and report them back to dagre for layout.
  return (
    <g
      onClick={node.onClick ?? undefined}
      style={{ cursor: node.onClick ? 'pointer' : 'default', userSelect: 'none' }}
    >
      {/* Background rect */}
      <rect
        width={NODE_WIDTH}
        height={NODE_HEIGHT}
        rx={6}
        ry={6}
        fill={bgColor}
        stroke={isFocused ? K.springGreen : 'none'}
        strokeWidth={isFocused ? 2 : 0}
      />
      {/* Kind label — small caps above */}
      <text
        x={NODE_WIDTH / 2}
        y={18}
        textAnchor="middle"
        alignmentBaseline="middle"
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fill: kindTextColor,
        }}
      >
        {node.kind}
      </text>
      {/* Entity name / title */}
      <text
        x={NODE_WIDTH / 2}
        y={38}
        textAnchor="middle"
        alignmentBaseline="middle"
        style={{
          fontSize: 11,
          fontWeight: 500,
          fill: textColor,
        }}
      >
        {label.length > 22 ? `${label.slice(0, 21)}…` : label}
      </text>
    </g>
  );
}
